// Pure aggregation of door-to-door registrations into per-area summary rows
// (the concept doc's buurt/wijk summary sheet). Percentage bases follow the
// spreadsheet: brief gepost is % of all homes; deurgesprek is % of posted homes;
// ja/nee/misschien/meedoet are % of door conversations; koop/huur are % of the
// chosen homes.
//
// Uitgebreid voor Zutphen Energie (2026-10): redenen per uitkomst, diensten,
// koop/huur, woningen in het gebied (uit PDOK) en straten nog te doen / bezig /
// klaar. 'Woningen in het gebied' is null — onbekend — zolang de lijst uit het
// register er niet compleet is: een te laag getal zou in een verantwoording
// overgeschreven worden.
//
// AI-CONTEXT: See docs/features/wervingsacties-registratie.md (Uitbreiding 2026-10 (2))
// AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero

import { isHuur } from './registratieOptions'
import { straatKey } from './straatKey'
import { dienstenOf } from './resolveWervingsDiensten'

const UITKOMSTEN = ['ja', 'nee', 'misschien']

/**
 * @typedef {object} StratenStand
 * @property {number | null} nog - Geen enkel gekozen adres bezocht, of niets gekozen; null zonder lijst uit het register
 * @property {number} bezig - Een deel bezocht
 * @property {number} klaar - Elk gekozen adres bezocht
 * @property {number | null} totaal - null zonder lijst uit het register
 */

/**
 * @typedef {object} RollupOpties
 * @property {(r: object) => string} [labelFn] - Leesbare naam van de groep; standaard de sleutel
 * @property {Array<object> | null} [adressen] - Alle adressen in het gebied (useWijkAdressen), alleen als die lijst compleet is
 */

// Geen basis = geen percentage (null), niet 0: '0%' zegt dat er niets gebeurde,
// terwijl er niets was om een percentage van te nemen.
// AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero
const pct = (num, denom) => (denom > 0 ? Math.round((num / denom) * 100) : null)

const emptyMetrics = () => ({
    totaal: 0,
    briefGepost: 0,
    deurgesprek: 0,
    ja: 0,
    nee: 0,
    misschien: 0,
    details: { ja: {}, nee: {}, misschien: {} },
    diensten: {},
    huur: 0,
    koop: 0,
    inGebied: 0,
    // straatKey → { gekozen, bezocht }
    straten: new Map(),
})

/**
 * @param {object} r - Wervingsregistratie
 * @returns {boolean} Er is iets gebeurd aan de deur: brief of gesprek
 */
const bezocht = (r) => r.BriefGepost === true || r.Deurgesprek === true

const straatVan = (m, r) => {
    const key = straatKey(r)
    if (!key) return null
    if (!m.straten.has(key)) m.straten.set(key, { gekozen: 0, bezocht: 0 })
    return m.straten.get(key)
}

const accumulate = (m, r) => {
    m.totaal += 1
    if (r.BriefGepost) m.briefGepost += 1
    if (r.Deurgesprek) m.deurgesprek += 1
    if (UITKOMSTEN.includes(r.Uitkomst)) {
        m[r.Uitkomst] += 1
        if (r.UitkomstDetail) {
            const per = m.details[r.Uitkomst]
            per[r.UitkomstDetail] = (per[r.UitkomstDetail] || 0) + 1
        }
    }
    // Diensten horen bij een 'ja'; registratieUpdate leegt ze bij elke andere uitkomst.
    if (r.Uitkomst === 'ja') {
        for (const key of dienstenOf(r)) m.diensten[key] = (m.diensten[key] || 0) + 1
    }
    // Sinds T11 kiest de werver sociale of particuliere huur; een oude
    // registratie zegt alleen 'huur'. Voor deze telling is het allemaal huur.
    // AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T11)
    if (isHuur(r.Woningtype)) m.huur += 1
    else if (r.Woningtype === 'koop') m.koop += 1

    const straat = straatVan(m, r)
    if (straat) {
        straat.gekozen += 1
        if (bezocht(r)) straat.bezocht += 1
    }
    return m
}

const accumulateGebied = (m, adres) => {
    m.inGebied += 1
    straatVan(m, adres)
}

/**
 * Zonder complete lijst uit het register ontbreken de straten waar niets is
 * gekozen; 'nog te doen' en het totaal zijn dan onbekend (null), niet te laag.
 * Bezig en klaar komen alleen uit gekozen adressen en zijn wel bekend.
 *
 * @param {Map<string, { gekozen: number, bezocht: number }>} straten
 * @param {boolean} metGebied
 * @returns {StratenStand}
 */
const stratenStand = (straten, metGebied) => {
    const stand = { nog: 0, bezig: 0, klaar: 0, totaal: 0 }
    for (const { gekozen, bezocht: gedaan } of straten.values()) {
        stand.totaal += 1
        if (gekozen > 0 && gedaan >= gekozen) stand.klaar += 1
        else if (gedaan > 0) stand.bezig += 1
        else stand.nog += 1
    }
    return metGebied ? stand : { ...stand, nog: null, totaal: null }
}

/**
 * @param {ReturnType<typeof emptyMetrics>} m
 * @param {boolean} metGebied - Of er een complete lijst uit het register was
 */
const finish = (m, metGebied) => ({
    totaal: m.totaal,
    briefGepost: m.briefGepost,
    deurgesprek: m.deurgesprek,
    ja: m.ja,
    nee: m.nee,
    misschien: m.misschien,
    meedoet: m.ja,
    details: m.details,
    diensten: m.diensten,
    huur: m.huur,
    koop: m.koop,
    woningtypeOnbekend: m.totaal - m.huur - m.koop,
    inGebied: metGebied ? m.inGebied : null,
    straten: stratenStand(m.straten, metGebied),
    briefGepostPct: pct(m.briefGepost, m.totaal),
    deurgesprekPct: pct(m.deurgesprek, m.briefGepost),
    jaPct: pct(m.ja, m.deurgesprek),
    neePct: pct(m.nee, m.deurgesprek),
    misschienPct: pct(m.misschien, m.deurgesprek),
    meedoetPct: pct(m.ja, m.deurgesprek),
    huurPct: pct(m.huur, m.totaal),
    koopPct: pct(m.koop, m.totaal),
})

/**
 * Het overzicht van een wervingsactie, gegroepeerd met `keyFn`.
 *
 * @param {Array<object>} registraties - De levende Wervingsregistraties
 * @param {(r: object) => string} keyFn - Groepssleutel; werkt op een registratie én op een adres uit PDOK
 * @param {RollupOpties} [opties]
 * @returns {{ rows: Array<object>, totals: object }}
 */
const buildRollup = (registraties, keyFn, { labelFn, adressen } = {}) => {
    const groups = new Map()
    const totals = emptyMetrics()
    const metGebied = Array.isArray(adressen)

    const groep = (item) => {
        const key = keyFn(item) || ''
        if (!groups.has(key)) groups.set(key, { key, label: labelFn ? labelFn(item) : key, metrics: emptyMetrics() })
        return groups.get(key)
    }

    for (const r of registraties || []) {
        accumulate(totals, r)
        accumulate(groep(r).metrics, r)
    }
    if (metGebied) {
        for (const adres of adressen) {
            accumulateGebied(totals, adres)
            accumulateGebied(groep(adres).metrics, adres)
        }
    }

    const rows = Array.from(groups.values())
        .map((g) => ({ key: g.key, label: g.label, ...finish(g.metrics, metGebied) }))
        // De groep zonder sleutel ('Overig', 'Zonder postcode') onderaan: een
        // terugvaltekst is geen naam om tussen te sorteren.
        // AI-CONTEXT: See docs/pitfalls.md#ui-display-fallback-used-as-sort-key
        .sort((a, b) => (a.key === '') - (b.key === '') || String(a.label).localeCompare(String(b.label), 'nl'))

    return { rows, totals: finish(totals, metGebied) }
}

export { buildRollup }
